import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Alert, Box } from '@mui/material';
import { useUser } from './UserContext';
import api from './api';

const SnappyPage = () => {
  const { user } = useUser();
  const { clubShortName, contestShortName } = useParams();
  const navigate = useNavigate();

  const [contest, setContest] = useState(null);
  const [club, setClub] = useState(null);
  const [allPhotos, setAllPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const photosRef = useRef([]);
  const spinSpeedsRef = useRef([]);
  const anglesRef = useRef([]);
  const velocitiesRef = useRef([]);
  const animationIdRef = useRef(null);
  const expandIntervalRef = useRef(null);
  const expansionTimeoutRef = useRef(null);
  const playOrderRef = useRef([]);
  const playCursorRef = useRef(0);


  // Load contest data
  useEffect(() => {
    const fetchContestData = async () => {
      try {
        setLoading(true);

        // Fetch contest details
        const contestResponse = await api.get(`/club/${clubShortName}/contest/${contestShortName}`, {
          headers: { 'Authentication': user.authentication }
        });
        setContest(contestResponse.data.result);

        // Fetch club
        const clubResponse = await api.get(`/club/${clubShortName}`, {
          headers: { 'Authentication': user.authentication }
        });
        setClub(clubResponse.data.result);

        // Fetch photos
        const photosResponse = await api.get(`/club/${clubShortName}/contest/${contestShortName}/photos`, {
          headers: { 'Authentication': user.authentication }
        });

        const photosData = photosResponse.data.result || {};
        setAllPhotos(photosData.allPhotos || []);

      } catch (err) {
        setError('Failed to load contest data');
        console.error('Error fetching contest:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.authentication && clubShortName && contestShortName) {
      fetchContestData();
    }
  }, [user, clubShortName, contestShortName]);

  // Background poll — refresh photo list every 60 seconds
  useEffect(() => {
    if (!user?.authentication || !clubShortName || !contestShortName) return;

    const poll = async () => {
      try {
        const res = await api.get(`/club/${clubShortName}/contest/${contestShortName}/photos`, {
          headers: { 'Authentication': user.authentication }
        });
        const fresh = res.data.result?.allPhotos || [];
        setAllPhotos(prev => {
          if (prev.length === fresh.length && fresh.every(p => prev.some(q => q.photoGuid === p.photoGuid)))
            return prev;
          return fresh;
        });
      } catch {
        // silent — showcase keeps running
      }
    };

    const id = setInterval(poll, 60_000);
    return () => clearInterval(id);
  }, [user, clubShortName, contestShortName]);

  // Initialize animation when photos are loaded
  useEffect(() => {
    if (allPhotos.length === 0) return;

    const containerWidth = window.innerWidth;
    const containerHeight = window.innerHeight;
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;

    // Clear any existing photos
    photosRef.current.forEach(photoElement => {
      if (photoElement && photoElement.parentNode) {
        photoElement.parentNode.removeChild(photoElement);
      }
    });

    // Reset arrays
    photosRef.current = [];
    spinSpeedsRef.current = [];
    anglesRef.current = [];
    velocitiesRef.current = [];

    // Create photo elements
    allPhotos.forEach((photo, i) => {
      const photoContainer = document.createElement("div");
      photoContainer.className = "photo-container";
      photoContainer.style.position = "absolute";
      photoContainer.style.width = "150px";
      photoContainer.style.transformOrigin = "center center";
      photoContainer.style.pointerEvents = "auto"; // Enable click events
      photoContainer.style.cursor = "pointer";
      photoContainer.style.transition = "transform 1s ease";

      const img = document.createElement("img");
      img.src = photo.photoUrl;
      img.style.width = "150px";
      img.style.height = "auto";
      img.style.border = "4px solid white";
      img.style.boxShadow = "0 0 10px rgba(0,0,0,0.7)";
      img.style.display = "block";
      img.style.borderRadius = "4px";

      const title = document.createElement("div");
      title.className = "photo-title";
      title.textContent = photo.photoTitle ?? '';
      title.style.color = "white";
      title.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
      title.style.padding = "4px 8px";
      title.style.fontSize = "12px";
      title.style.textAlign = "center";
      title.style.borderRadius = "0 0 4px 4px";
      title.style.maxWidth = "142px"; // 150px - 8px padding
      title.style.overflow = "hidden";
      title.style.textOverflow = "ellipsis";
      title.style.whiteSpace = "nowrap";

      photoContainer.appendChild(img);
      photoContainer.appendChild(title);

      // Add click handler with proper closure
      (function(index) {
        photoContainer.addEventListener('click', () => {
          expandPhoto(index);
        });
      })(i);

      // Start at center
      photoContainer.style.left = `${centerX - 75}px`;
      photoContainer.style.top = `${centerY - 75}px`;

      document.body.appendChild(photoContainer);
      photosRef.current.push(photoContainer);

      // Set initial spin and motion
      spinSpeedsRef.current.push((Math.random() * 0.001 + 0.001) * (Math.random() < 0.5 ? -1 : 1));
      anglesRef.current.push(0);

      // Set initial random velocity
      velocitiesRef.current.push({
        vx: (Math.random() - 0.5) * 2.666,
        vy: (Math.random() - 0.5) * 2.666
      });

      // Animate from center to a scattered location
      setTimeout(() => {
        const x = Math.random() * (containerWidth - 200);
        const y = Math.random() * (containerHeight - 200);
        photoContainer.style.transition = 'left 1s ease, top 1s ease';
        photoContainer.style.left = `${x}px`;
        photoContainer.style.top = `${y}px`;

        // Store original position for later use
        photoContainer.setAttribute('data-original-x', x);
        photoContainer.setAttribute('data-original-y', y);
      }, 50);
    });

    // Start animation
    const animate = () => {
      photosRef.current.forEach((photoContainer, i) => {
        if (!photoContainer.classList.contains("expanded")) {
          const angle = anglesRef.current[i] += spinSpeedsRef.current[i];
          const velocity = velocitiesRef.current[i];

          // Get current position
          let x = parseFloat(photoContainer.style.left);
          let y = parseFloat(photoContainer.style.top);

          // Update position
          x += velocity.vx;
          y += velocity.vy;

          const rect = photoContainer.getBoundingClientRect();

          // Bounce logic
          if (x <= 0 || x + rect.width >= containerWidth) velocity.vx *= -1;
          if (y <= 0 || y + rect.height >= containerHeight) velocity.vy *= -1;

          photoContainer.style.left = `${x}px`;
          photoContainer.style.top = `${y}px`;

          // Apply rotation
          photoContainer.style.transform = `rotate(${angle}rad)`;
        }
      });
      animationIdRef.current = requestAnimationFrame(animate);
    };

    animate();


    // Expand next photo in randomized non-repeating order
    const ensurePlayOrder = () => {
      const n = photosRef.current.length;
      if (n === 0) return;
      // rebuild if sizes mismatch or empty
      if (!playOrderRef.current || playOrderRef.current.length !== n) {
        playOrderRef.current = Array.from({ length: n }, (_, i) => i);
        // Fisher–Yates
        for (let i = n - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [playOrderRef.current[i], playOrderRef.current[j]] = [playOrderRef.current[j], playOrderRef.current[i]];
        }
        playCursorRef.current = 0;
      }
    };

    const expandNextPhoto = () => {
      if (expansionTimeoutRef.current) return;
      if (photosRef.current.length === 0) return;
      ensurePlayOrder();
      const n = playOrderRef.current.length;
      if (n === 0) return;
      // If we've reached the end, reshuffle and start over
      if (playCursorRef.current >= n) {
        // Reshuffle
        for (let i = n - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [playOrderRef.current[i], playOrderRef.current[j]] = [playOrderRef.current[j], playOrderRef.current[i]];
        }
        playCursorRef.current = 0;
      }
      const targetIndex = playOrderRef.current[playCursorRef.current++];
      expandPhoto(targetIndex);
    };
    const expandPhoto = (targetIndex) => {
      if (photosRef.current.length === 0) return;

      const photoContainer = photosRef.current[targetIndex];
      const img = photoContainer.querySelector('img');
      const title = photoContainer.querySelector('.photo-title');
      const photo = allPhotos[targetIndex];

      const doExpand = () => {
        // Create a temporary high-res image for measuring
        const tempImg = new Image();
        tempImg.src = photo.photoUrl; // Use full resolution image

        // Wait for high-res image to load before expanding
        tempImg.onload = () => {
          // Get the current accumulated angle and normalize it to 0-2π range
          const currentAngle = anglesRef.current[targetIndex];
          const normalizedAngle = ((currentAngle % (2 * Math.PI)) + (2 * Math.PI)) % (2 * Math.PI);

          // Find the shortest path to 0 (either clockwise or counterclockwise)
          let targetRotation = 0;
          if (normalizedAngle > Math.PI) {
            // If angle > π, it's shorter to rotate to 2π (which is 0)
            targetRotation = 2 * Math.PI;
          }

          // Calculate expanded dimensions to fit within window with 50px margin
          const maxWidth = window.innerWidth - 100;  // 50px margin on each side
          const maxHeight = window.innerHeight - 100; // 50px margin on each side
          const imgAspect = tempImg.naturalWidth / tempImg.naturalHeight;

          let expandedWidth, expandedHeight;
          if (imgAspect > maxWidth / maxHeight) {
            expandedWidth = maxWidth;
            expandedHeight = maxWidth / imgAspect;
          } else {
            expandedHeight = maxHeight;
            expandedWidth = maxHeight * imgAspect;
          }

          // Get base position (parse from style, which is the untransformed position)
          const baseX = parseFloat(photoContainer.style.left);
          const baseY = parseFloat(photoContainer.style.top);

          const centerX = window.innerWidth / 2;
          const centerY = window.innerHeight / 2;

          // Calculate translation from base position to center
          const dx = centerX - (baseX + expandedWidth / 2);
          const dy = centerY - (baseY + expandedHeight / 2);

          photoContainer.classList.add("expanded");
          photoContainer.style.transition = "transform 1s ease, width 1s ease, height 1s ease";
          photoContainer.style.transform = `translate(${dx}px, ${dy}px) rotate(${targetRotation}rad)`;
          photoContainer.style.zIndex = "9999";

          // Set container width to match expanded image
          photoContainer.style.width = `${expandedWidth}px`;

          // Switch to high-resolution image and set expanded dimensions
          img.src = photo.photoUrl;
          img.style.width = `${expandedWidth}px`;
          img.style.height = `${expandedHeight}px`;

          // Update title to show full format: Title : Location : Photographer (omitting nulls), partway through the expansion
          const expandedTitle = [photo.photoTitle, photo.photoLocation, photo.photoPhotographer].filter(Boolean).join(' : ');
          title.textContent = expandedTitle;
          title.style.maxWidth = `${expandedWidth - 8}px`; // Match expanded width minus padding

          // Update the angle to match where we actually rotated to
          anglesRef.current[targetIndex] = targetRotation;

          expansionTimeoutRef.current = setTimeout(() => {
            photoContainer.classList.remove("expanded");
            photoContainer.style.transition = "transform 1s ease, width 1s ease, height 1s ease";
            photoContainer.style.transform = `rotate(${anglesRef.current[targetIndex]}rad)`;

            // Restore original size
            photoContainer.style.width = "150px";
            img.style.transition = "width 2s ease, height 2s ease";
            img.style.width = "150px";
            img.style.height = "auto";

            // Restore title to show only photo title
            title.textContent = photo.photoTitle ?? '';
            title.style.maxWidth = "142px"; // Restore original maxWidth

            setTimeout(() => {
              photoContainer.style.zIndex = "auto";
              expansionTimeoutRef.current = null;
            },2000);
          }, 5000);
        };
      };

      // Start the expansion process
      doExpand();
    };

    // Start expanding photos
    expandIntervalRef.current = setInterval(expandNextPhoto, 10000);

    // Cleanup function
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (expandIntervalRef.current) {
        clearInterval(expandIntervalRef.current);
      }
      photosRef.current.forEach(photoContainer => {
        if (photoContainer && photoContainer.parentNode) {
          photoContainer.parentNode.removeChild(photoContainer);
        }
      });
    };
  }, [allPhotos]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (expandIntervalRef.current) {
        clearInterval(expandIntervalRef.current);
      }
      photosRef.current.forEach(photoContainer => {
        if (photoContainer && photoContainer.parentNode) {
          photoContainer.parentNode.removeChild(photoContainer);
        }
      });
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#111',
        color: 'white'
      }}>
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (error || !contest) {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#111',
        p: 4
      }}>
        <Alert severity="error">{error || 'Contest not found'}</Alert>
      </Box>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      margin: 0,
      padding: 0,
      background: '#111',
      overflow: 'hidden',
    }}>
      {/* Background mascot */}
      <div style={{
        position: 'absolute',
        left: '0px',
        top: '0px',
        width: '100%',
        opacity: '5%',
        pointerEvents: 'none',
        zIndex: 1
      }}>
        <img src='/mascot.png' alt='' style={{ width: '100%', height: 'auto' }} />
      </div>

      {/* Back button */}
      <div
        onClick={() => navigate(`/club/${clubShortName}/contest/${contestShortName}`)}
        style={{
          position: 'absolute',
          top: '16px',
          right: '20px',
          color: 'white',
          zIndex: 10,
          cursor: 'pointer',
          fontSize: '14px',
          opacity: 0.7,
          userSelect: 'none',
        }}
      >
        ← Back
      </div>

      {/* Contest info */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        color: 'white',
        zIndex: 2,
        pointerEvents: 'none'
      }}>
        <div style={{
          fontSize: '18px',
          fontWeight: 'bold',
          marginBottom: '4px'
        }}>
          {club?.clubName}
        </div>
        <div style={{
          fontSize: '15px',
          fontWeight: 'bold',
          marginBottom: '4px'
        }}>
          {contest.contestName}
        </div>
        <div style={{
          fontSize: '12px',
          maxWidth: '300px',
          lineHeight: '1.0'
        }}>
          {contest.contestDescription}
        </div>
      </div>
    </div>
  );
};

export default SnappyPage;